import { createFileRoute, Link } from "@tanstack/react-router";
import ogHomeImg from "@/assets/og/og-home.jpg";
import { useMemo, useState } from "react";
import { GUIDES } from "@/data/guides";
import { GUIDE_HUB_META } from "@/lib/guide-hub-meta";
import { BookOpen, ArrowRight, Clock, Search, Compass } from "lucide-react";
import { hreflangLinks, ogImageMeta, SITE_URL } from "@/lib/seo";
import { bilingualSidecars } from "@/lib/i18n-head";
import { useT } from "@/i18n/LanguageProvider";

export const Route = createFileRoute("/guides/")({
  head: () => {
    const url = "https://jantrobazar.com/guides";
    const title = "Buying Guides — Motorcycle & Car Parts | Jantro Bazar";
    const titleBn = "কেনাকাটার গাইড — মোটরসাইকেল ও গাড়ির পার্টস | Jantro Bazar";
    const description =
      "Independent buying guides for Bangladeshi riders: brake pads, engine oil, chain kits, spotting fake parts, and buying safely on cash on delivery.";
    const descriptionBn =
      "বাংলাদেশি রাইডারদের জন্য স্বাধীন কেনাকাটা গাইড: ব্রেক প্যাড, ইঞ্জিন অয়েল, চেইন কিট, নকল পার্টস চেনা এবং ক্যাশ অন ডেলিভারিতে নিরাপদে কেনাকাটা।";
    const collectionLd = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "Jantro Bazar Buying Guides",
      description,
      url,
      inLanguage: ["en", "bn"],
      isPartOf: { "@type": "WebSite", name: "Jantro Bazar", url: "https://jantrobazar.com" },
    };
    const breadcrumbLd = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://jantrobazar.com/" },
        { "@type": "ListItem", position: 2, name: "Guides", item: url },
      ],
    };
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:site_name", content: "Jantro Bazar" },
        { property: "og:type", content: "website" },
        { property: "og:title", content: "Buying Guides — Jantro Bazar" },
        {
          property: "og:description",
          content: "Practical how-to and buying advice for motorcycle and car parts in Bangladesh.",
        },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: "Buying Guides — Jantro Bazar" },
        { name: "twitter:description", content: description },
        ...ogImageMeta(`${SITE_URL}${ogHomeImg}`, "Guides — Jantro Bazar"),
        ...bilingualSidecars({ titleEn: title, titleBn, descEn: description, descBn: descriptionBn }),
      ],
      links: [{ rel: "canonical", href: url }, ...hreflangLinks(url)],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(collectionLd) },
        { type: "application/ld+json", children: JSON.stringify(breadcrumbLd) },
      ],
    };
  },
  errorComponent: () => (
    <div className="p-16 text-center text-muted-foreground" aria-busy="true">
      Failed to load guides.
    </div>
  ),
  notFoundComponent: () => (
    <div className="p-16 text-center text-muted-foreground">Guides not found.</div>
  ),
  component: GuidesIndex,
});

function GuidesIndex() {
  const [category, setCategory] = useState<string>("");
  const [q, setQ] = useState<string>("");
  const { lang } = useT();
  const isBn = lang === "bn";

  const categories = useMemo(() => {
    const map = new Map<string, string>();
    for (const g of GUIDES) {
      const key = g.category_slug ?? g.category_label;
      if (!map.has(key)) map.set(key, g.category_label);
    }
    return Array.from(map.entries()); // [slug, label]
  }, []);

  // Guides grouped by their `category_slug` — used to surface `/c/{slug}` deep
  // pages ("Browse by category") right below the topic hubs. Only categories
  // whose slug looks like a catalog slug (present as `category_slug` on ≥1
  // guide) render; label falls back to the guide's category_label.
  const guideCategoryGroups = useMemo(() => {
    const map = new Map<string, { label: string; count: number }>();
    for (const g of GUIDES) {
      if (!g.category_slug) continue;
      const cur = map.get(g.category_slug);
      if (cur) {
        cur.count += 1;
      } else {
        map.set(g.category_slug, { label: g.category_label, count: 1 });
      }
    }
    return Array.from(map.entries()).sort((a, b) => b[1].count - a[1].count);
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return GUIDES.filter((g) => {
      if (category) {
        const key = g.category_slug ?? g.category_label;
        if (key !== category) return false;
      }
      if (needle) {
        const hay = `${g.title_en} ${g.title_bn} ${g.description}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [category, q]);

  return (
    <main className="container-app py-10 flex-1">
      <div className="max-w-2xl">
        <div className="inline-flex items-center gap-2 text-xs font-medium text-accent latin-caps tracking-wide">
          <BookOpen className="h-3.5 w-3.5" /> {isBn ? "যন্ত্র বাজার গাইড" : "Jantro Bazar Guides"}
        </div>
        <h1 className={`mt-3 text-3xl md:text-4xl font-bold text-primary ${isBn ? "" : ""}`}>
          {isBn ? "কেনাকাটা ও রক্ষণাবেক্ষণ গাইড" : "Buying & maintenance guides"}
        </h1>
        <p className="mt-3 text-muted-foreground">
          {isBn
            ? "ঢাকা, চট্টগ্রাম ও সিলেটে কর্মরত মেকানিকদের সরাসরি পরামর্শ — যারা প্রথমবারেই সঠিক খরচ করতে চান তাদের জন্য।"
            : "Straight-talking advice from mechanics working in Dhaka, Chattogram, and Sylhet — written for riders who want to spend right the first time."}
        </p>
      </div>

      {/* Browse by topic — hub cards */}
      <section className="mt-10">
        <div className="flex items-center gap-2 text-sm font-semibold text-primary mb-4">
          <Compass className="h-4 w-4 text-accent" aria-hidden />
          {isBn ? "বিষয় অনুযায়ী দেখুন" : "Browse by topic"}
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {GUIDE_HUB_META.map((hub) => (
            <Link
              key={hub.slug}
              to={hub.path}
              className="group rounded-xl border border-border bg-card p-4 hover:border-accent/50 hover:shadow-sm transition"
            >
              <div className="flex items-start justify-between gap-2">
                <h3 className="text-sm font-semibold text-primary group-hover:text-accent transition">
                  {isBn ? hub.h1_bn : hub.h1_en}
                </h3>
                <ArrowRight
                  className="h-4 w-4 text-muted-foreground group-hover:text-accent flex-shrink-0 mt-0.5"
                  aria-hidden
                />
              </div>
              <p className="mt-1.5 text-xs text-muted-foreground line-clamp-2">
                {isBn ? hub.meta_desc_bn : hub.meta_desc_en}
              </p>
            </Link>
          ))}
        </div>
      </section>

      {/* Browse by category */}
      {guideCategoryGroups.length > 0 && (
        <section className="mt-10">
          <div className="flex items-center gap-2 text-sm font-semibold text-primary mb-4">
            <Compass className="h-4 w-4 text-accent" aria-hidden />
            {isBn ? "পার্ট ক্যাটাগরি অনুযায়ী দেখুন" : "Browse by part category"}
          </div>
          <div className="flex flex-wrap gap-2">
            {guideCategoryGroups.map(([slug, { label, count }]) => (
              <Link
                key={slug}
                to="/c/$slug"
                params={{ slug }}
                className="group inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 h-9 text-xs font-semibold text-primary hover:border-accent/60 hover:text-accent transition"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-accent" aria-hidden />
                {label}
                <span className="rounded-full bg-secondary px-1.5 py-0.5 text-[10px] font-bold text-muted-foreground group-hover:text-accent">
                  {count}
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Search + category filter */}
      <div className="mt-10 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <label className="relative w-full md:max-w-sm">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
            aria-hidden
          />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={isBn ? "গাইড খুঁজুন…" : "Search guides…"}
            aria-label={isBn ? "গাইড খুঁজুন" : "Search guides"}
            className="w-full h-10 pl-9 pr-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </label>
        <div
          className="flex flex-wrap gap-2"
          role="group"
          aria-label={isBn ? "ক্যাটাগরি অনুযায়ী ফিল্টার" : "Filter by category"}
        >
          <button
            type="button"
            onClick={() => setCategory("")}
            aria-pressed={category === ""}
            className={`px-3 h-8 rounded-full border text-xs font-medium transition ${category === "" ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border text-muted-foreground hover:text-foreground"}`}
          >
            {isBn ? "সব" : "All"}
          </button>
          {categories.map(([slug, label]) => (
            <button
              key={slug}
              type="button"
              onClick={() => setCategory(slug)}
              aria-pressed={category === slug}
              className={`px-3 h-8 rounded-full border text-xs font-medium transition ${category === slug ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border text-muted-foreground hover:text-foreground"}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <p className="mt-10 text-sm text-muted-foreground">
          {isBn
            ? "আপনার অনুসন্ধানের সাথে কোনো গাইড মেলে না — ফিল্টার মুছে দেখুন।"
            : "No guides match your search yet — try clearing filters."}
        </p>
      ) : (
        <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((g) => (
            <Link
              key={g.slug}
              to="/guides/$slug"
              params={{ slug: g.slug }}
              className="group rounded-xl border border-border bg-card p-5 hover:border-accent/40 hover:shadow-md transition"
            >
              <div className="text-xs font-medium text-accent uppercase tracking-wide">
                {g.category_label}
              </div>
              <h2 className="mt-2 text-lg font-semibold text-primary group-hover:text-accent transition">
                {isBn ? g.title_bn : g.title_en}
              </h2>
              <p className="mt-2 text-sm text-muted-foreground line-clamp-3">{g.description}</p>
              <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <Clock className="h-3 w-3" /> {g.reading_minutes}{" "}
                  {isBn ? "মিনিট পড়া" : "min read"}
                </span>
                <span className="inline-flex items-center gap-1 text-accent font-medium">
                  {isBn ? "পড়ুন" : "Read"} <ArrowRight className="h-3 w-3" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </main>
  );
}
